import { createContext } from "@lit/context";
import type { Engine } from "../../engine/Engine";
import {
  CommandRegistry,
  Disposable,
  InputRouter,
  ObservableStore,
  TypedEventHub,
} from "../events/messaging/index";

export interface ViewportEventDetail {
  readonly host: HTMLElement;
  readonly engine: Engine;
}

export interface ViewportEventMap {
  "viewport-ready": ViewportEventDetail;
  "viewport-disposing": ViewportEventDetail;
}

export interface ViewportCommandMap {}

export interface ViewportState {
  readonly phase: "ready" | "disposed";
}

export class ViewportContext implements Disposable {
  readonly events = new TypedEventHub<ViewportEventMap>();
  readonly commands = new CommandRegistry<ViewportCommandMap>();
  readonly state = new ObservableStore<ViewportState>({ phase: "ready" });
  readonly input: InputRouter<GlobalEventHandlersEventMap>;

  private readonly controller = new AbortController();
  private _disposed = false;

  constructor(
    readonly host: HTMLElement,
    readonly engine: Engine,
  ) {
    this.input = new InputRouter(engine.canvas);
  }

  get signal(): AbortSignal {
    return this.controller.signal;
  }

  get disposed(): boolean {
    return this._disposed;
  }

  announceReady(): void {
    if (this._disposed) return;
    this.events.emit("viewport-ready", {
      host: this.host,
      engine: this.engine,
    });
  }

  dispose(): void {
    if (this._disposed) return;
    this._disposed = true;

    const errors: unknown[] = [];
    const run = (callback: () => void) => {
      try {
        callback();
      } catch (error) {
        errors.push(error);
      }
    };

    run(() =>
      this.events.emit("viewport-disposing", {
        host: this.host,
        engine: this.engine,
      }),
    );
    run(() => this.state.set({ phase: "disposed" }));
    this.controller.abort(new Error("ViewportContext has been disposed"));
    run(() => this.input.dispose());
    run(() => this.commands.dispose());
    run(() => this.state.dispose());
    run(() => this.events.dispose());

    if (errors.length === 1) throw errors[0];
    if (errors.length > 1) {
      throw new AggregateError(errors, "ViewportContext disposal failed");
    }
  }
}

export const viewportContext = createContext<ViewportContext | undefined>(
  Symbol.for("sugartech.viewport-context"),
);
